import { FC, memo, useCallback, useMemo } from "react";
import { FormikHandlers } from "formik";
import { TextField } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { AutocompleteFilterBodyProps } from "../AutocompleteFilterBody";
import {
  FilterItem,
  Operator,
  ResourceType,
  useGetResourceTypesByTypeAndFilterKeyTypeQuery,
} from "@/services/cloudchipr.api";
import { DropdownSelect } from "@/components/common/select/dropdown-select/DropdownSelect";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { FilterTriggerComponentProps } from "@/components/common/select/dropdown-select/utils/types/filterTriggerComponentProps";

interface SchemeFilterProps extends AutocompleteFilterBodyProps {
  filter: FilterItem;
  operators?: Operator[];
  onChange: FormikHandlers["handleChange"];
  resourceType: ResourceType;
}

export const SchemeFilter: FC<SchemeFilterProps> = memo(
  ({ operators, filter, onChange, resourceType, setFieldValue, ...props }) => {
    const { data, isLoading } = useGetResourceTypesByTypeAndFilterKeyTypeQuery({
      type: resourceType,
      filterKeyType: "scheme",
    });

    const selectedValue = useMemo(() => {
      const value = filter?.value;

      const arrayValue = value && typeof value === "string" ? [value] : [];
      return Array.isArray(value) ? value : arrayValue;
    }, [filter?.value]);

    const changeHandler = useCallback(
      (values: string[]) => {
        const value = values[0];
        setFieldValue("value", value);
        return setFieldValue("value", value);
      },
      [setFieldValue],
    );

    const renderOptions = useMemo(() => {
      return data?.map((item) => {
        return {
          value: item,
          label: <TypographyWithTooltip title={item} />,
        };
      });
    }, [data]);

    return (
      <DropdownSelect
        singleSelect
        {...props}
        options={renderOptions ?? []}
        disabled={isLoading}
        label="Value"
        TriggerComponent={TriggerComponent}
        values={selectedValue}
        submitHandlerOnClose={changeHandler}
      />
    );
  },
);

const TriggerComponent: FC<FilterTriggerComponentProps> = ({
  selectedValues,
  onClick,
  label,
  error,
  disabled,
  open,
}) => {
  return (
    <TextField
      fullWidth
      label={label}
      size="xsmall"
      focused={open}
      error={!!error}
      disabled={disabled}
      onClick={onClick}
      value={selectedValues.at(0)}
      sx={{ minWidth: 135, width: 135 }}
      InputLabelProps={{ shrink: !!selectedValues.length || open }}
      InputProps={{
        readOnly: true,
        endAdornment: open ? (
          <ArrowDropUpIcon fontSize="medium" color="action" />
        ) : (
          <ArrowDropDownIcon fontSize="medium" color="action" />
        ),
        sx: { pr: 1, cursor: "pointer" },
        inputProps: { sx: { cursor: "pointer" } },
      }}
    />
  );
};
